/**
 * 背景音乐 Composable
 * 管理游戏背景音乐的自动播放、音量控制等
 */

import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useSettingsStore } from '@/stores'
import { getLocaleAudio, SOUND_FILES } from '@/utils/locale-audio'

/**
 * 背景音乐管理 Hook
 */
export function useBgMusic() {
  const { locale } = useI18n()
  const route = useRoute()
  const settingsStore = useSettingsStore()

  // 背景音乐是视讯专属功能: 只在 /live/* 页面播放 (不含视讯登录页), 大厅/登录页不播放
  // (之前只判断"是否登录页", 导致设置恢复后新大厅首页也会被拉起视讯背景音乐)
  const isLiveRoute = computed(() => route.path.startsWith('/live') && route.name !== 'live-login')

  // 当前播放的 Audio 实例
  const currentAudio = ref<HTMLAudioElement | null>(null)

  // 是否正在播放
  const isPlaying = ref(false)

  // 当前播放的音乐索引
  const currentIndex = ref(0)

  // 默认音量
  const defaultVolume = 0.3

  // 背景音乐文件映射
  const bgmFileMap: Record<string, string> = {
    game_bg1: SOUND_FILES.BGM.GAME_BG1,
    game_bg2: SOUND_FILES.BGM.GAME_BG2,
    game_bg3: SOUND_FILES.BGM.GAME_BG3,
  }

  /**
   * 获取用户选中的背景音乐列表
   */
  const getSelectedBgmFiles = (): string[] => {
    const selectedList = settingsStore.bgmList
    if (!selectedList || selectedList.length === 0) {
      return []
    }
    return selectedList.map(id => bgmFileMap[id]).filter(Boolean) as string[]
  }

  /**
   * 播放背景音乐
   * 播放用户选中的背景音乐
   */
  const play = async (): Promise<void> => {
    // 只在视讯页面播放背景音乐 (大厅/登录页不播放)
    if (!isLiveRoute.value) {
      return
    }

    // 检查背景音乐是否开启
    if (!settingsStore.isBgMusicEnabled) {
      return
    }

    // 获取用户选中的音乐列表
    const bgmFiles = getSelectedBgmFiles()
    if (bgmFiles.length === 0) {
      return
    }

    // 确保索引有效
    if (currentIndex.value < 0 || currentIndex.value >= bgmFiles.length) {
      currentIndex.value = 0
    }

    try {
      // 停止当前播放
      stop()

      // 获取音频文件路径 (默认使用中文)
      const filename = bgmFiles[currentIndex.value]
      if (!filename) {
        console.warn('[BgMusic] 无效的音乐索引:', currentIndex.value)
        return
      }
      const audioUrl = await getLocaleAudio(filename, (locale.value || 'zh') as string)

      // 创建新的 Audio 实例
      const audio = new Audio(audioUrl)
      audio.volume = defaultVolume
      audio.loop = false // 不循环单曲,播放完自动切换下一首

      currentAudio.value = audio

      // 监听播放错误
      audio.addEventListener('error', error => {
        console.error(`[BgMusic] 播放失败: ${bgmFiles[currentIndex.value]}`, error)
        isPlaying.value = false
        // 尝试播放下一首
        playNext()
      })

      // 监听播放结束,自动播放下一首
      audio.addEventListener('ended', () => {
        playNext()
      })

      // 播放音频
      await audio.play()
      isPlaying.value = true
    } catch (error) {
      console.error(`[BgMusic] 播放失败: ${bgmFiles[currentIndex.value]}`, error)
      isPlaying.value = false
    }
  }

  /**
   * 播放下一首 (内部方法)
   */
  const playNext = async (): Promise<void> => {
    const bgmFiles = getSelectedBgmFiles()
    if (bgmFiles.length === 0) {
      stop()
      return
    }
    currentIndex.value = (currentIndex.value + 1) % bgmFiles.length
    await play()
  }

  /**
   * 暂停播放
   */
  const pause = (): void => {
    if (currentAudio.value && !currentAudio.value.paused) {
      currentAudio.value.pause()
      isPlaying.value = false
    }
  }

  /**
   * 恢复播放
   */
  const resume = async (): Promise<void> => {
    if (currentAudio.value && currentAudio.value.paused && settingsStore.isBgMusicEnabled && isLiveRoute.value) {
      try {
        await currentAudio.value.play()
        isPlaying.value = true
      } catch (error) {
        console.error('[BgMusic] 恢复播放失败:', error)
      }
    }
  }

  /**
   * 停止播放
   */
  const stop = (): void => {
    if (currentAudio.value) {
      currentAudio.value.pause()
      currentAudio.value.currentTime = 0
      currentAudio.value = null
      isPlaying.value = false
    }
  }

  /**
   * 设置音量
   *
   * @param volume - 音量 (0-1)
   */
  const setVolume = (volume: number): void => {
    const clampedVolume = Math.max(0, Math.min(1, volume))
    if (currentAudio.value) {
      currentAudio.value.volume = clampedVolume
    }
  }

  /**
   * 监听背景音乐开关变化
   */
  watch(
    () => settingsStore.isBgMusicEnabled,
    enabled => {
      if (enabled) {
        // 开启时,检查是否有选中的音乐
        const bgmFiles = getSelectedBgmFiles()
        if (bgmFiles.length === 0) {
          return
        }

        // 如果没有正在播放,则开始播放
        if (!currentAudio.value) {
          play()
        } else {
          resume()
        }
      } else {
        // 关闭时暂停
        pause()
      }
    }
  )

  /**
   * 监听背景音乐列表变化
   * 用户在设置中点击"确认"按钮后会更新 bgmList
   */
  watch(
    () => settingsStore.bgmList,
    (newList, oldList) => {
      // 只有列表真正发生变化时才处理
      if (JSON.stringify(newList) === JSON.stringify(oldList)) {
        return
      }

      // 如果背景音乐开启,重新播放新选择的音乐
      if (settingsStore.isBgMusicEnabled && newList.length > 0) {
        currentIndex.value = 0 // 重置到第一首
        play()
      } else if (newList.length === 0) {
        // 如果用户取消了所有选择,停止播放
        stop()
      }
    }
  )

  /**
   * 监听路由变化
   * 离开视讯页面 (进大厅/登录页) 时暂停音乐, 进入视讯页面时恢复播放
   */
  watch(isLiveRoute, isLive => {
    if (!isLive) {
      pause()
    } else if (settingsStore.isBgMusicEnabled) {
      // 进入视讯页面且音乐开关开启时,恢复或开始播放
      if (currentAudio.value) {
        resume()
      } else {
        play()
      }
    }
  })

  /**
   * 监听语言变化
   */
  watch(
    () => locale.value,
    () => {
      // 语言变化时,重新加载当前音乐
      if (isPlaying.value) {
        play()
      }
    }
  )

  /**
   * 组件卸载时清理
   */
  onUnmounted(() => {
    stop()
  })

  return {
    // 状态
    isPlaying,
    currentIndex,

    // 方法
    play,
    pause,
    resume,
    stop,
    setVolume,
  }
}
